import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, Heart, Flag, Trash2, Send, MessageCircle, Clock, User
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_COLORS = {
  general: "bg-[#8FA69A]/15 text-[#2C4A3B]",
  meditation: "bg-[#D4AF37]/15 text-[#8B7520]",
  experiences: "bg-[#C17B5F]/15 text-[#8B4530]",
  questions: "bg-[#6B8EAE]/15 text-[#3A5F7F]",
  techniques: "bg-[#9B8EC4]/15 text-[#5E4F8B]",
  "book-club": "bg-[#E8A87C]/15 text-[#A0623B]",
};

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function ForumPostPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/forum/posts/${id}`),
      axios.get(`${API}/forum/posts/${id}/comments`),
    ]).then(([postRes, commentsRes]) => {
      setPost(postRes.data);
      setComments(commentsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/forum/posts/${id}/comments`, { content: newComment });
      setComments((prev) => [...prev, data]);
      setNewComment("");
      toast.success("Comment added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add comment");
    } finally {
      setSending(false);
    }
  };

  const handleLike = async () => {
    if (!user) { toast.error("Sign in to like posts"); return; }
    try {
      const { data } = await axios.post(`${API}/forum/posts/${id}/like`);
      setPost((prev) => ({
        ...prev,
        likes: data.liked ? (prev.likes || 0) + 1 : Math.max((prev.likes || 0) - 1, 0),
        liked_by: data.liked
          ? [...(prev.liked_by || []), user.id || user._id]
          : (prev.liked_by || []).filter((uid) => uid !== (user.id || user._id)),
      }));
    } catch { toast.error("Failed"); }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    try {
      await axios.post(`${API}/forum/posts/${id}/report`, { reason: reportReason });
      toast("Report submitted. Thank you.");
      setShowReport(false);
      setReportReason("");
    } catch { toast.error("Failed to report"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-loading">
        <div className="w-8 h-8 rounded-full border-2 border-[#8FA69A] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-not-found">
        <p className="text-[#737E79]">Post not found.</p>
      </div>
    );
  }

  const isLiked = post.liked_by?.includes(user?.id || user?._id);

  return (
    <main className="min-h-screen" data-testid="forum-post-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 pt-8 pb-16">
        <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-[#737E79] hover:text-[#1A221E] transition-colors mb-6" data-testid="back-to-forum">
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Community
        </Link>

        {/* Post */}
        <div className="p-6 rounded-2xl bg-white border border-[#E3DDD2] mb-6" data-testid="post-detail">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#8FA69A]/10 flex items-center justify-center">
              <span className="text-base font-medium text-[#8FA69A]">{(post.author_name || "A")[0].toUpperCase()}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A221E]">{post.author_name}</p>
              <p className="text-xs text-[#737E79]">{timeAgo(post.created_at)}</p>
            </div>
            <Badge className={`${CAT_COLORS[post.category] || CAT_COLORS.general} border-0 rounded-full text-xs ml-auto`}>
              {post.category}
            </Badge>
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#1A221E] mb-3">{post.title}</h1>
          <p className="text-base text-[#737E79] leading-relaxed whitespace-pre-wrap">{post.content}</p>

          <div className="flex items-center gap-4 mt-6 pt-4 border-t border-[#E3DDD2]">
            <button
              onClick={handleLike}
              data-testid="like-post-btn"
              className={`flex items-center gap-1.5 text-sm transition-colors ${isLiked ? "text-[#D4AF37]" : "text-[#737E79] hover:text-[#D4AF37]"}`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? "fill-[#D4AF37]" : ""}`} strokeWidth={1.5} />
              {post.likes || 0}
            </button>
            <span className="flex items-center gap-1.5 text-sm text-[#737E79]">
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              {comments.length}
            </span>
            {user && (
              <button
                onClick={() => setShowReport(true)}
                data-testid="report-post-btn"
                className="flex items-center gap-1.5 text-sm text-[#737E79] hover:text-red-500 transition-colors ml-auto"
              >
                <Flag className="w-4 h-4" strokeWidth={1.5} />
                Report
              </button>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="mb-6">
          <h2 className="font-heading text-lg font-medium text-[#1A221E] mb-4">
            Comments ({comments.length})
          </h2>
          {comments.length === 0 ? (
            <p className="text-sm text-[#737E79]" data-testid="no-comments">No comments yet. Be the first to respond.</p>
          ) : (
            <div className="space-y-3" data-testid="comments-list">
              {comments.map((comment) => (
                <div key={comment.id} className="p-4 rounded-2xl bg-white border border-[#E3DDD2]" data-testid={`comment-${comment.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-[#EAE2D6] flex items-center justify-center">
                      <span className="text-xs font-medium text-[#737E79]">{(comment.author_name || "A")[0].toUpperCase()}</span>
                    </div>
                    <span className="text-sm font-medium text-[#1A221E]">{comment.author_name}</span>
                    <span className="text-xs text-[#737E79]">{timeAgo(comment.created_at)}</span>
                  </div>
                  <p className="text-sm text-[#737E79] leading-relaxed">{comment.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add comment */}
        {user ? (
          <form onSubmit={handleComment} className="flex gap-3" data-testid="comment-form">
            <Input
              data-testid="comment-input"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts..."
              disabled={sending}
              className="flex-1 rounded-full bg-white border-[#E3DDD2]"
            />
            <button
              type="submit"
              disabled={sending || !newComment.trim()}
              data-testid="submit-comment-btn"
              className="w-10 h-10 rounded-full bg-[#8FA69A] text-white flex items-center justify-center hover:bg-[#2C4A3B] transition-all disabled:opacity-50 shrink-0"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </form>
        ) : (
          <div className="p-4 rounded-2xl bg-[#F0EDE6] text-center" data-testid="comment-login-prompt">
            <p className="text-sm text-[#737E79]">
              <Link to="/login" className="text-[#8FA69A] font-medium hover:text-[#2C4A3B]">Sign in</Link> to join the conversation.
            </p>
          </div>
        )}
      </div>

      {/* Report Dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="rounded-2xl bg-white border-[#E3DDD2] max-w-md" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#1A221E]">Report Post</DialogTitle>
            <DialogDescription className="text-sm text-[#737E79]">Help us maintain a safe, respectful community.</DialogDescription>
          </DialogHeader>
          <textarea
            data-testid="report-reason-input"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Why are you reporting this post?"
            rows={3}
            className="w-full rounded-xl bg-white border border-[#E3DDD2] p-3 text-sm resize-none focus:ring-[#8FA69A] focus:border-[#8FA69A] focus:outline-none"
          />
          <button
            onClick={handleReport}
            data-testid="submit-report-btn"
            className="w-full py-2.5 bg-red-500 text-white rounded-full text-sm font-medium hover:bg-red-600 transition-all"
          >
            Submit Report
          </button>
        </DialogContent>
      </Dialog>
    </main>
  );
}
